<template>
  <article>
    <h2 class="history-title mb-3">
      {{ title }}
    </h2>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col md="6">
            <enum-select-field
              v-model="checkTypes"
              :all-selected.sync="allCheckTypesSelected"
              :loading="loading"
              label="Тип"
              select-all-label="Все типы"
              :enum-class="checkTypeClass"
              multiple
              hide-details
              outlined
              @change="fetchFromFirstPage"
            />
          </v-col>
          <v-col md="6">
            <enum-select-field
              v-model="checkStatuses"
              :all-selected.sync="allCheckStatusesSelected"
              :loading="loading"
              label="Статус"
              select-all-label="Все статусы"
              :enum-class="checkStatusClass"
              multiple
              hide-details
              outlined
              @change="fetchFromFirstPage"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :page="page"
        :items-per-page="itemsPerPage"
        :server-items-length="itemsLength"
        :loading="loading"
        fixed-header
        :footer-props="footerProps"
        @update:options="handleTableOptionsUpdate"
      >
        <template #item.order="{ value }">
          <router-link
            v-if="value"
            tag="a"
            style="font-size: 14px;"
            :to="getOrderHref(value.id)"
          >
            #{{ value.code }}
          </router-link>
          <span v-else>—</span>
        </template>
        <template #item.type="{ value }">
          {{ value || '—' }}
        </template>
        <template #item.error="{ value }">
          {{ value || '—' }}
        </template>
        <template #item.status="{ item, value }">
          <v-chip
            v-if="value"
            text-color="white"
            :color="getStatusColor(item)"
            small
          >
            {{ getStatusName(item) }}
          </v-chip>
          <v-chip
            v-if="item.correctionReceiptId"
            class="ml-1"
            text-color="white"
            color="blue-grey"
            small
            title="Эмулированный чек исправлен чеком коррекции"
          >
            Исправлен чеком #{{ item.correctionReceiptId }}
          </v-chip>
          <v-chip
            v-if="item.sourceReceiptId"
            class="ml-1"
            text-color="white"
            color="blue-grey"
            small
            title="Чек коррекции по эмулированному чеку"
          >
            Коррекция чека #{{ item.sourceReceiptId }}
          </v-chip>

          <!--          <v-chip
            v-if="item.repeatFromId"
            class="ml-1"
            text-color="white"
            :color="getStatusColor(item)"
            small
          >
            Повтор чека {{ item.repeatFromId }}
          </v-chip>-->

          <!--          <v-chip
            v-if="item.repeatId"
            class="ml-1"
            text-color="white"
            color="grey"
            small
          >
            Создан повтор:{{ item.repeatId }}
          </v-chip>-->
        </template>
        <template #item.created_at="{ value }">
          {{ formatDate(value) }}
        </template>
        <template #item.actions="{ item }">
          <v-btn
            v-if="item.canBeRepeated"
            color="success"
            depressed
            small
            :disabled="repeatCommandLoading && item.id!==repeatedCommandId"
            :loading="repeatCommandLoading && item.id===repeatedCommandId"
            @click="repeatCommand(item.id)"
          >
            Повторить
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </article>
</template>

<script>
  import { mapGetters } from 'vuex'
  import printApi from '@/api/print'
  import snackbarMessage from '@/mixins/snackbar-message'
  import EnumSelectField from '@/components/ui/selects/EnumSelectField.vue'
  import CheckType from '@/models/enums/cashbox/CheckType'
  import { websocketService } from '@/services/WebsocketService'
  import { getPrintKkmServerChannel } from '@/util/websocket-channels'
  import { KKM_COMMAND_CREATED, KKM_COMMAND_UPDATED } from '@/constants/websocket-events'
  import CheckStatus from '@/models/enums/cashbox/CheckStatus'

  export default {
    name: 'CashBoxHistory',
    components: { EnumSelectField },
    mixins: [snackbarMessage],
    props: {
      date: String,
    },
    data () {
      return {
        headers: [
          { text: 'Номер заказа', value: 'order', align: 'center', sortable: false },

          { text: 'Тип', value: 'typeDescription', align: 'start', sortable: false },
          { text: 'Статус', value: 'status', align: 'start', sortable: false },
          { text: 'Ошибка', value: 'error', sortable: false },
          { text: 'ID', value: 'id', align: 'center' },
          { text: 'Дата', value: 'created_at', align: 'start' },
          { text: 'Действия', value: 'actions', align: 'start' },
        ],
        footerProps: { 'items-per-page-options': [15, 25, 50, 100, -1] },
        items: [],
        itemsLength: 0,
        itemsPerPage: 50,
        sortBy: [],
        sortDesc: [],
        page: 1,
        loading: false,
        menu: false,
        checkTypes: [],
        allCheckTypesSelected: true,
        checkStatuses: [],
        allCheckStatusesSelected: true,
        repeatCommandLoading: false,
        repeatedCommandId: null,
      }
    },
    computed: {
      ...mapGetters({
        localStorageBranch: 'branches/getLocalStorageBranch',
      }),
      title () {
        const date = new Date(this.date).toLocaleString(this.$i18n.locale, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })

        return `История печати за ${date}`
      },
      checkTypeClass () {
        return CheckType
      },
      checkStatusClass () {
        return CheckStatus
      },
    },
    watch: {
      date () {
        this.fetchFromFirstPage()
      },
    },
    created () {
      this.fetchHistory()
      this.addWsListeners()
    },
    beforeDestroy () {
      websocketService.getEcho()
        .channel(getPrintKkmServerChannel(this.localStorageBranch.id))
        .stopListening(KKM_COMMAND_CREATED, this.fetchHistory)
        .stopListening(KKM_COMMAND_UPDATED, this.fetchHistory)
    },
    methods: {
      addWsListeners () {
        websocketService.getEcho()
          .channel(getPrintKkmServerChannel(this.localStorageBranch.id))
          .listen(KKM_COMMAND_CREATED, this.fetchHistory)
          .listen(KKM_COMMAND_UPDATED, this.fetchHistory)
      },
      async fetchHistory () {
        this.loading = true
        try {
          const response = await printApi.fetchHistory({
            filialId: this.localStorageBranch.id,
            page: this.page,
            types: this.checkTypes,
            allTypes: this.allCheckTypesSelected,
            statuses: this.checkStatuses,
            allStatuses: this.allCheckStatusesSelected,
            date: this.date,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc,
            limit: this.itemsPerPage,
          })
          this.items = response.items.slice()
          this.itemsLength = response.meta.total
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.loading = false
        }
      },

      fetchFromFirstPage () {
        this.page = 1
        this.fetchHistory()
      },
      handleTableOptionsUpdate (options) {
        this.itemsPerPage = options.itemsPerPage
        this.page = options.page
        this.sortBy = options.sortBy
        this.sortDesc = options.sortDesc

        this.fetchHistory(options.page)
      },
      formatDate (date) {
        return new Date(date).toLocaleString(this.$i18n.locale, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })
      },
      getStatusName (item) {
        return CheckStatus.getName(item.status)
      },
      getOrderHref (id) {
        return this.$router.resolve({ name: 'fastOrderEdit', query: { id } }).href
      },
      getStatusColor (item) {
        if (item.repeatId) {
          return 'grey'
        }
        switch (item.status) {
          case CheckStatus.Complete: return 'green'

          case CheckStatus.Unknown: return 'orange'

          case CheckStatus.Fail:
          case CheckStatus.CancelByTimeout:
          case CheckStatus.Cancel:
            return 'red'

          default: return 'grey'
        }
      },
      async repeatCommand (id) {
        this.repeatCommandLoading = true
        this.repeatedCommandId = id
        try {
          const receipt = await printApi.repeatCommand(id)
          this.replaceItemInList(receipt)
          this.showSuccessMessage('Команда на повтор отправлена и будет выполнена в ближайшее время')
        } catch (err) {
          this.showErrorFromResponse(err)
        }
        this.repeatCommandLoading = false
        this.repeatedCommandId = null
      },
      replaceItemInList (newItem) {
        this.items = this.items.map(item => item.id === newItem.id ? newItem : item)
      },
    },
  }
</script>

<style lang="less" scoped>
.history-title {
  font-size: 20px;
  font-weight: 500;
}
</style>
